import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { startTransition, useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchProjectMatrix, } from '@/entities/agent';
import { fetchBrandManagers, } from '@/entities/brand-manager';
import { boardReturnToParam, clearBoardFilterParams, matchesMatrixRow, METRIC_FILTER_KEYS, parseMatrixFilters, sortBoardItems, writeMatrixFilters, } from '@/features/board-filters';
import { useAgentScope } from '@/features/agent-scope';
import { ApiError } from '@/shared/api';
import { uiCopy } from '@/shared/copy';
import { Alert, AlertDescription, AlertTitle, Button, MetaStrip, Skeleton, StageTabs, } from '@/shared/ui';
import { AgentScopeToggle } from '@/widgets/agent-scope-toggle';
import { PROJECT_ATTENDANCE_STAGES, groupRowsByProjectAttendanceStage, parseAttendanceMode, parseAttendanceStage, pluralEngagements, projectAttendanceStageLabel, } from '../lib/project-attendance-stage';
import { AttendanceBoard } from './attendance-board';
import { AttendanceFilters } from './attendance-filters';
import { AttendanceFunnelTable } from './attendance-funnel-table';
import { AttendanceMatrix } from './attendance-matrix';
import { AttendanceModeTabs } from './attendance-mode-tabs';
function cellForProject(row, projectCode) {
    return row.cells.find((cell) => cell.projectCode === projectCode);
}
/** Project query changes the row set only for these filters. */
function projectAffectsRowFilter(filters) {
    if (filters.priority && filters.project)
        return true;
    if (filters.otherProjectNotices)
        return true;
    if (!filters.project)
        return false;
    if (filters.metricScope !== 'selected')
        return false;
    return METRIC_FILTER_KEYS.some((key) => filters.metrics[key] !== 'all');
}
export function EngagementProjectsPage() {
    const navigate = useNavigate();
    const { scope } = useAgentScope();
    const [searchParams, setSearchParams] = useSearchParams();
    const filters = useMemo(() => parseMatrixFilters(searchParams), [searchParams]);
    const mode = parseAttendanceMode(searchParams.get('mode'));
    const stageFilter = parseAttendanceStage(searchParams.get('stage'));
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [managers, setManagers] = useState([]);
    const [helpOpen, setHelpOpen] = useState(false);
    const currentManagerId = managers.find((item) => item.isCurrent)?.managerId ?? null;
    const managerNameById = useMemo(() => {
        const map = new Map();
        for (const manager of managers) {
            map.set(manager.managerId, manager.displayName);
        }
        return map;
    }, [managers]);
    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setData(await fetchProjectMatrix({ scope }));
        }
        catch (err) {
            setError(err instanceof ApiError ? err.message : uiCopy.actionFailed);
            setData(null);
        }
        finally {
            setLoading(false);
        }
    }, [scope]);
    useEffect(() => {
        void load();
    }, [load]);
    useEffect(() => {
        let cancelled = false;
        void fetchBrandManagers()
            .then((response) => {
            if (!cancelled)
                setManagers(response.items);
        })
            .catch(() => {
            if (!cancelled)
                setManagers([]);
        });
        return () => {
            cancelled = true;
        };
    }, []);
    const selectedProject = useMemo(() => {
        const projects = data?.projects ?? [];
        if (projects.length === 0)
            return null;
        return (projects.find((project) => project.code === filters.project) ??
            projects[0] ??
            null);
    }, [data, filters.project]);
    useEffect(() => {
        if (!data?.projects.length)
            return;
        if (searchParams.get('project')?.trim())
            return;
        const first = data.projects[0];
        if (!first)
            return;
        const next = new URLSearchParams(searchParams);
        next.set('project', first.code);
        setSearchParams(next, { replace: true });
    }, [data, searchParams, setSearchParams]);
    const agencies = useMemo(() => {
        const names = new Set();
        for (const row of data?.rows ?? []) {
            const name = row.agencyName?.trim();
            if (name)
                names.add(name);
        }
        return [...names]
            .sort((a, b) => a.localeCompare(b, 'ru'))
            .map((name) => ({ value: name, label: name }));
    }, [data]);
    const rowFilterIdentity = useMemo(() => {
        const metricsKey = METRIC_FILTER_KEYS.map((key) => filters.metrics[key]).join('|');
        return [
            filters.q,
            filters.responsible,
            filters.agency,
            filters.status,
            filters.mine ? '1' : '0',
            filters.sort,
            filters.metricScope,
            filters.priority ? '1' : '0',
            filters.otherProjectNotices ? '1' : '0',
            filters.ratingMin,
            filters.ratingMax,
            filters.engagementMin,
            filters.engagementMax,
            filters.externalMin,
            filters.externalMax,
            metricsKey,
            projectAffectsRowFilter(filters) ? filters.project : '',
        ].join('\0');
    }, [filters]);
    const visibleRows = useMemo(() => {
        if (!data)
            return [];
        const matched = data.rows.filter((row) => matchesMatrixRow(row, filters, currentManagerId));
        return sortBoardItems(matched, filters.sort);
        // filters.project omitted from deps when it does not change the row set
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, currentManagerId, rowFilterIdentity]);
    const summary = useMemo(() => {
        if (!selectedProject) {
            return { visited: 0, notices: 0 };
        }
        let visited = 0;
        let notices = 0;
        for (const row of visibleRows) {
            const cell = cellForProject(row, selectedProject.code);
            if (cell?.visited)
                visited += 1;
            if (cell && cell.notices > 0)
                notices += 1;
        }
        return { visited, notices };
    }, [visibleRows, selectedProject]);
    const rowsByStage = useMemo(() => {
        const empty = {
            project_invite: [],
            project_booked: [],
            project_visited: [],
        };
        if (!selectedProject)
            return empty;
        if (mode !== 'funnel' && mode !== 'board')
            return empty;
        return groupRowsByProjectAttendanceStage(visibleRows, selectedProject.code);
    }, [visibleRows, selectedProject, mode]);
    const stageTabItems = useMemo(() => {
        if (!selectedProject || mode !== 'funnel')
            return [];
        const shortName = selectedProject.shortName || selectedProject.name;
        return [
            {
                id: 'all',
                label: 'Общий список',
                count: visibleRows.length,
            },
            ...PROJECT_ATTENDANCE_STAGES.map((stage) => ({
                id: stage,
                label: projectAttendanceStageLabel(stage, shortName),
                count: rowsByStage[stage].length,
            })),
        ];
    }, [visibleRows.length, selectedProject, rowsByStage, mode]);
    const applyFilters = useCallback((next) => {
        setSearchParams(writeMatrixFilters(searchParams, next), { replace: true });
    }, [searchParams, setSearchParams]);
    function resetFilters() {
        const cleared = clearBoardFilterParams(searchParams);
        const first = data?.projects[0];
        if (first)
            cleared.set('project', first.code);
        setSearchParams(cleared, { replace: true });
    }
    function setMode(next) {
        const params = new URLSearchParams(searchParams);
        if (next === 'matrix')
            params.delete('mode');
        else
            params.set('mode', next);
        if (next !== 'funnel')
            params.delete('stage');
        setSearchParams(params, { replace: true });
    }
    function setStage(next) {
        const params = new URLSearchParams(searchParams);
        params.set('mode', 'funnel');
        if (next === 'all')
            params.delete('stage');
        else
            params.set('stage', next);
        setSearchParams(params, { replace: true });
    }
    function setProject(code) {
        startTransition(() => {
            applyFilters({ ...filters, project: code });
        });
    }
    const returnToRef = useRef('');
    returnToRef.current = boardReturnToParam(`/engagements/projects?${searchParams.toString()}`);
    const openCycle = useCallback((displayNumber) => {
        navigate(`/engagements/cycles/${encodeURIComponent(displayNumber)}?returnTo=${returnToRef.current}`);
    }, [navigate]);
    function openStageFromBoard(stage) {
        setStage(stage);
    }
    const emptyMessage = data && data.rows.length === 0
        ? 'Активных циклов нет.'
        : 'По выбранным фильтрам строк нет. Нажмите «Сбросить».';
    const projectShortName = selectedProject?.shortName || selectedProject?.name || '';
    return (_jsxs("div", { className: "space-y-3 px-0 py-1", children: [_jsxs("header", { className: "flex flex-wrap items-start justify-between gap-3", children: [_jsx("div", { className: "space-y-1", children: _jsx("h1", { className: "text-xl font-semibold", children: "\u041F\u043E\u0441\u0435\u0449\u0435\u043D\u043D\u043E\u0441\u0442\u044C \u043F\u0440\u043E\u0435\u043A\u0442\u0430" }) }), _jsx(AgentScopeToggle, {})] }), _jsx(AttendanceModeTabs, { mode: mode, projectCode: selectedProject?.code ?? filters.project, projects: data?.projects ?? [], helpOpen: helpOpen, onModeChange: setMode, onProjectChange: setProject, onHelpOpenChange: setHelpOpen, helpContent: _jsxs(_Fragment, { children: [_jsxs("p", { children: [_jsx("b", { children: "\u0411\u0422" }), " \u2014 \u0431\u0440\u043E\u043A\u0435\u0440-\u0442\u0443\u0440; ", _jsx("b", { children: "\u041C\u0435\u0440." }), " \u2014 \u0434\u0440\u0443\u0433\u043E\u0435 \u043E\u0447\u043D\u043E\u0435 \u043C\u0435\u0440\u043E\u043F\u0440\u0438\u044F\u0442\u0438\u0435;", ' ', _jsx("b", { children: "\u041A\u043B\u0438\u0435\u043D\u0442" }), " \u2014 \u043E\u0447\u043D\u0430\u044F \u0432\u0441\u0442\u0440\u0435\u0447\u0430 \u0441 \u043A\u043B\u0438\u0435\u043D\u0442\u043E\u043C, \u041C\u041E\u041F \u043F\u043E\u0434\u0442\u0432\u0435\u0440\u0434\u0438\u043B \u043F\u0440\u0438\u0441\u0443\u0442\u0441\u0442\u0432\u0438\u0435 \u0430\u0433\u0435\u043D\u0442\u0430."] }), _jsxs("p", { children: [_jsx("b", { children: "\u0423" }), " \u2014 \u0443\u0432\u0435\u0434\u043E\u043C\u043B\u0435\u043D\u0438\u044F \u043D\u0430 \u043F\u0440\u043E\u0435\u043A\u0442. ", _jsx("b", { children: "\u041B" }), " \u2014 \u043B\u0438\u0434\u044B \u0438\u0437 \u043D\u0438\u0445.", ' ', _jsx("b", { children: "\u0412" }), " \u2014 \u043B\u0438\u0434\u044B \u0441 \u043F\u0440\u043E\u0432\u0435\u0434\u0451\u043D\u043D\u043E\u0439 \u0432\u0441\u0442\u0440\u0435\u0447\u0435\u0439. ", _jsx("b", { children: "\u0411" }), " \u2014 \u0441\u043E\u0437\u0434\u0430\u043D\u043D\u044B\u0435 \u043D\u0435\u0442\u0435\u0445\u043D\u0438\u0447\u0435\u0441\u043A\u0438\u0435 \u0431\u0440\u043E\u043D\u0438. ", _jsx("b", { children: "\u0421" }), " \u2014 \u0442\u0435\u0445\u043D\u0438\u0447\u0435\u0441\u043A\u0438\u0435 \u0441\u0434\u0435\u043B\u043A\u0438 ACTIVE \u0437\u0430 \u043F\u0435\u0440\u0438\u043E\u0434 \u0446\u0438\u043A\u043B\u0430, \u043D\u0435 \u0444\u0430\u043A\u0442 \u043F\u043E\u0434\u043F\u0438\u0441\u0430\u043D\u0438\u044F \u0434\u043E\u0433\u043E\u0432\u043E\u0440\u0430."] }), _jsx("p", { children: "\u0421\u0442\u0440\u043E\u043A\u0430 \u2014 \u0430\u043A\u0442\u0438\u0432\u043D\u044B\u0439 \u0446\u0438\u043A\u043B. \u0427\u0435\u0442\u044B\u0440\u0435 \u043F\u0440\u043E\u0435\u043A\u0442\u0430 \u043A\u0430\u0442\u0430\u043B\u043E\u0433\u0430. \u0421\u0447\u0451\u0442\u0447\u0438\u043A \u0448\u0430\u043F\u043A\u0438 \u2014 \u0447\u0438\u0441\u043B\u043E \u0446\u0438\u043A\u043B\u043E\u0432 \u043F\u043E\u0441\u043B\u0435 \u0444\u0438\u043B\u044C\u0442\u0440\u0430." }), data?.dealsCaption ? _jsx("p", { children: data.dealsCaption }) : null] }) }), _jsx(AttendanceFilters, { filters: filters, managers: managers, agencies: agencies, projectShortName: projectShortName, onChange: applyFilters, onReset: resetFilters }), _jsx(MetaStrip, { primary: `${visibleRows.length} ${pluralEngagements(visibleRows.length)}`, items: [
                    _jsx(_Fragment, { children: "\u0410\u043A\u0442\u0438\u0432\u043D\u044B\u0435" }),
                    ...(selectedProject
                        ? [
                            _jsxs(_Fragment, { children: [projectShortName, ": \u043F\u043E\u0441\u0435\u0442\u0438\u043B\u0438 ", _jsx("b", { children: summary.visited }), ' · ', "\u0441 \u0443\u0432\u0435\u0434\u043E\u043C\u043B\u0435\u043D\u0438\u044F\u043C\u0438 ", _jsx("b", { children: summary.notices })] }),
                        ]
                        : []),
                    ...(data && visibleRows.length !== data.total
                        ? [_jsxs(_Fragment, { children: ["(\u0438\u0437 ", data.total, " \u043D\u0430 \u043C\u0430\u0442\u0440\u0438\u0446\u0435)"] })]
                        : []),
                ] }), error ? (_jsxs(Alert, { variant: "destructive", children: [_jsx(AlertTitle, { children: uiCopy.errorTitle }), _jsx(AlertDescription, { children: error }), _jsx(Button, { type: "button", variant: "outline", className: "mt-3", onClick: () => void load(), children: uiCopy.retry })] })) : null, loading ? (_jsx(Skeleton, { className: "h-64 w-full" })) : data && selectedProject ? (_jsxs(_Fragment, { children: [mode === 'funnel' ? (_jsx(StageTabs, { items: stageTabItems, value: stageFilter, onValueChange: setStage })) : null, mode === 'matrix' ? (_jsx(AttendanceMatrix, { projects: data.projects, rows: visibleRows, emptyMessage: emptyMessage, managerNameById: managerNameById, onOpenCycle: openCycle })) : null, mode === 'funnel' ? (_jsx(AttendanceFunnelTable, { rows: visibleRows, projectCode: selectedProject.code, projectShortName: projectShortName, emptyMessage: emptyMessage, managerNameById: managerNameById, onOpenCycle: openCycle, stageFilter: stageFilter })) : null, mode === 'board' ? (_jsx(AttendanceBoard, { rows: visibleRows, rowsByStage: rowsByStage, projectCode: selectedProject.code, projectShortName: projectShortName, emptyMessage: emptyMessage, onOpenCycle: openCycle, onOpenStage: openStageFromBoard })) : null] })) : null] }));
}
